<template>
  <div class="wro-actionable-panel__body">
    <div class="wro-confirm-shipping">
      <h4 class="wro-confirm-shipping__block-title">Please address your parcel shipment to:</h4>
      <div class="wro-confirm-shipping__address">
        <p data-sb-automation="address" class="wro-confirm-shipping__address-line">
          {{ companyName }} <span v-if="!isPoweredBySubAccountUser">C/O ShipBob, Inc</span>
        </p>
        <p class="wro-confirm-shipping__address-line">{{ fulfillmentCenterAddress.address1 }}</p>
        <p class="wro-confirm-shipping__address-line">{{ fulfillmentCenterAddress.address2 }}</p>
        <p class="wro-confirm-shipping__address-line">{{ fulfillmentCenterAddress.city }}, {{ fulfillmentCenterAddress.state }}, {{ fulfillmentCenterAddress.zipCode }}</p>
        <p class="wro-confirm-shipping__address-line">Phone: {{ fulfillmentCenterAddress.phoneNumber | phone }}</p>
        <p v-if="!isPoweredBySubAccountUser" data-sb-automation="email" class="wro-confirm-shipping__address-line">
          Email: {{ fulfillmentCenterAddress.email }}
        </p>
      </div>
    </div>
    <div class="wro-confirm-shipping">
      <h4 class="wro-confirm-shipping__block-title wro-confirm-shipping__block-title--space-bottom">
        What is the estimated date of arrival for your shipment?
      </h4>
      <div class="wro-confirm-shipping__date-picker">
        <date-picker
          class="table table-bordered wro-confirm-shipping__date-picker-input"
          v-model="estimatedArrivalDate"
          id="arrival-details-date-picker"
        />
        <label for="arrival-details-date-picker" class="wro-confirm-shipping__date-picker-icon icon-calendar--after" />
      </div>
    </div>
    <div v-if="enableBOL" class="wro-confirm-shipping">
      <h4 class="wro-confirm-shipping__block-title">
        To speed up unloading, please upload your BOL (optional)
      </h4>
      <label
        for="bol-file"
        v-show="!bolFile"
        class="wro-confirm-shipping__file-upload-link wro-confirm-shipping__file-upload-link-container"
      >
        <span class="icon-upload-cloud wro-confirm-shipping__file-upload-link-icon" />
        <p class="wro-confirm-shipping__file-upload-link-text">Upload BOL</p>
      </label>
      <sb-file-upload
        class="wro-confirm-shipping__file-upload-link-container"
        id="bol-file"
        v-show="bolFile"
        v-model="bolFile"
        @invalidFile="handleInvalidFile"
        :allowedFileTypes="allowedFileTypes"
        @previewFile="handlePreviewFile"
      />
    </div>
  </div>
</template>

<script>
import ServiceTypes from '@/enums/modules/fulfillmentCenter/serviceType.js'
import toastr from 'toastr'
import DatePicker from '@/components/utils/PopoverDatePicker'
import { ModuleName } from '@/store/modules/createWarehouseReceivingOrder'

export default {
  name: 'EstimatedArrivalDate',
  inject: ['isPoweredBySubAccountUser'],
  components: {
    'date-picker': DatePicker
  },
  data: () => ({
    allowedFileTypes: ['.pdf', '.jpeg', '.jpg', '.png'],
    StoreModuleName: ModuleName
  }),
  watch: {
    bol (newBol, oldBol) {
      if (newBol && newBol.name && newBol.url !== oldBol.url) {
        this.$shipBobStore.dispatch(this.StoreModuleName + '/fetchBolFile', { url: newBol.url, name: newBol.name })
      }
    }
  },
  computed: {
    draft () {
      return this.$shipBobStore.getters[ModuleName + '/draft']
    },
    editWro () {
      return this.$shipBobStore.getters[ModuleName + '/editWro']
    },
    isWroCreated () {
      return this.$shipBobStore.getters[ModuleName + '/isWroCreated']
    },
    user () {
      return this.$shipBobStore.getters['user/getUser']
    },
    companyName () {
      return this.user.impersonatedCompanyName || this.user.companyName.value
    },
    bol () {
      return this.$shipBobStore.getters[this.StoreModuleName + '/billOfLading']
    },
    bolFile: {
      get () {
        return this.bol.file
      },
      set (newBol) {
        this.$shipBobStore.dispatch(this.StoreModuleName + '/setBillOfLading', newBol)
      }
    },
    estimatedArrivalDate: {
      get () {
        return this.$shipBobStore.getters[this.StoreModuleName + '/estimatedArrivalDate']
      },
      set (newVal) {
        this.$shipBobStore.dispatch(this.StoreModuleName + '/setEstimatedArrivalDate', newVal)
        if (this.isWroCreated && (this.draft.estimatedArrivalDate !== this.editWro.estimatedArrivalDate)) this.draft.hasEstimatedArrivalChanged = true
        else if (this.isWroCreated) this.draft.hasEstimatedArrivalChanged = false
      }
    },
    fulfillmentCenterAddress () {
      const receivingService = this.draft && this.draft.fulfillmentCenter && this.draft.fulfillmentCenter.getService(ServiceTypes.Receiving)
      return receivingService && receivingService.address || {} // eslint-disable-line no-mixed-operators
    },
    packagingType () {
      return this.draft ? this.draft.packagingType || {} : {}
    },
    enableBOL () {
      return this.packagingType && (this.packagingType.id === 2 || this.packagingType.id === 3)
    }
  },
  methods: {
    handleInvalidFile (file) {
      const allowedTypes = this.allowedFileTypes.join(', ')
      const msg = `${file.name} is an invalid file type. Allowed file types are: ${allowedTypes}`
      toastr.error(msg)
    },
    handlePreviewFile (file) {
      if (file.size === 0 && this.bol.url) window.open(this.bol.url)
      else window.open(URL.createObjectURL(file))
    }
  },
  filters: {
    phone (val = '') {
      const p = val.match(/(\d{3})(\d{3})(\d{4})$/)
      if (p) return `(${p[1]}) ${p[2]}-${p[3]}`
      return val
    }
  }
}
</script>

<style scoped lang='scss'>
  .wro-confirm-shipping {
    margin-bottom: 2em;
    &:last-of-type {
      margin-bottom: 0;
    }
    &__block-title {
      color: #4e5c73;
      margin: 0;
      &--space-bottom {
        margin-bottom: 1em;
      }
    }
    &__address {
      color: #727a87;
    }
    &__address-line {
      margin: 0;
    }
    &__date-picker {
      display: inline-flex;
      align-items: center;
      color: #2470af;
    }
    &__date-picker-input {
      border: none !important;
    }
    &__date-picker-icon {
      cursor: pointer;
      margin-left: 0.5em;
      margin-bottom: 0;
    }
    &__file-upload-link-container {
      display: inline-flex;
      align-items: center;
      margin: 0;
      margin-top: 1em;
      max-width: 100%;
    }
    &__file-upload-link {
      color: #2470af;
      font-weight: 600;
      cursor: pointer;
    }
    &__file-upload-link-icon {
      margin-right: 0.5em;
      font-size: 1.25em;
    }
    &__file-upload-link-text {
      margin: 0;
    }
  }
</style>
